import { NewsletterOrigin, NewsletterStatus, RegisterInputV2 } from '@spa/generated/graphql/graphql';
import { NewsletterRegistrationFormStatus } from './newsletterRegistrationStatus';
import registerSubscriberAction from '@spa/lib/actions/newsletter/registerSubscriberAction';
import { useState } from 'react';
export interface NewsletterRegistrationFields {
  readonly email: string;
}
export type NewsletterRegistrationValues<FormValues extends NewsletterRegistrationFields> = FormValues & {
  readonly [key: string]: string;
};
export interface UseNewsletterRegistrationOptions {
  readonly consent: string;
  readonly origin: NewsletterOrigin;
  readonly honeypotToken: string;
  readonly onRegistrationResult?: (email: string) => void;
  readonly trackNewsletterRegistration: () => void;
}
export interface UseNewsletterRegistrationResult<FormValues extends NewsletterRegistrationFields> {
  readonly formStatus: NewsletterRegistrationFormStatus;
  readonly handleSubmit: (values: NewsletterRegistrationValues<FormValues>) => Promise<void>;
  readonly resetSubmissionStatus: () => void;
  readonly submissionError: NewsletterStatus | null;
}
const useNewsletterRegistration = <FormValues extends NewsletterRegistrationFields,>({
  consent,
  origin,
  honeypotToken,
  onRegistrationResult,
  trackNewsletterRegistration
}: UseNewsletterRegistrationOptions): UseNewsletterRegistrationResult<FormValues> => {
  const [formStatus, setFormStatus] = useState(NewsletterRegistrationFormStatus.INITIAL);
  const [submissionError, setSubmissionError] = useState<NewsletterStatus | null>(null);
  const handleSubmit = async (values: NewsletterRegistrationValues<FormValues>): Promise<void> => {
    const {
      email
    } = values;
    setFormStatus(NewsletterRegistrationFormStatus.SUBMITTING);
    setSubmissionError(null);
    const honeypotValue = values[honeypotToken];
    if (honeypotValue === undefined || honeypotValue === null) {
      setFormStatus(NewsletterRegistrationFormStatus.SUBMITTED);
      setSubmissionError(NewsletterStatus.ERROR);
      return;
    }
    const registerInput: RegisterInputV2 = {
      email,
      origin,
      consent,
      metadata: [{
        key: honeypotToken,
        value: honeypotValue
      }],
      additionalParams: null
    };
    try {
      const {
        status
      } = await registerSubscriberAction('registerSubscriberAction', registerInput, honeypotToken);
      if (status !== NewsletterStatus.SUCCESS) {
        setSubmissionError(status);
      }
      onRegistrationResult?.(email);
      trackNewsletterRegistration();
    } catch {
      setSubmissionError(NewsletterStatus.ERROR);
    }
    setFormStatus(NewsletterRegistrationFormStatus.SUBMITTED);
  };
  const resetSubmissionStatus = (): void => {
    setFormStatus(NewsletterRegistrationFormStatus.INITIAL);
    setSubmissionError(null);
  };
  return {
    formStatus,
    handleSubmit,
    resetSubmissionStatus,
    submissionError
  };
};
export default useNewsletterRegistration;